import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { functions, storage, ensureAppCheckToken } from '../firebase/firebase';
import {
  BRANDING_ELIGIBLE_PLAN_IDS,
  parseHexColor,
  previewBrandingAccentOnly,
  getSidebarLogoDimensions,
  getSidebarLogoAlign,
  getSidebarLogoFrame,
  DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT,
  DEFAULT_SIDEBAR_LOGO_MAX_WIDTH,
  DEFAULT_SIDEBAR_LOGO_ALIGN,
  DEFAULT_LOGO_CORNER_RADIUS,
  LOGO_CORNER_RADIUS_MAX,
  sidebarLogoAlignToFlexJustify,
  SIDEBAR_LOGO_MAX_HEIGHT_MIN,
  SIDEBAR_LOGO_MAX_HEIGHT_MAX,
  SIDEBAR_LOGO_MAX_WIDTH_MIN,
  SIDEBAR_LOGO_MAX_WIDTH_MAX,
} from '../utils/branding';
import {
  companyBrandingLogoStoragePath,
  normalizeCompanyIdForStorage,
  COMPANY_BRANDING_LOGO_MAX_BYTES,
  COMPANY_BRANDING_LOGO_ACCEPT,
  isAllowedBrandingLogoFile,
} from '../utils/companyBrandingUpload';
import BrandingSandbox from './BrandingSandbox';
import BrandingColorField from './BrandingColorField';
import BrandedSidebarLogo from './BrandedSidebarLogo';
import {
  getTeamSignOffSheetStyleFromCompany,
  effectiveTeamSignOffSheetStyle,
} from '../utils/signingSheetBranding';

/** Crewchain app chrome (plan strip), not company accent. */
const HINT_CHROME = {
  panelBg: '#0f172a',
  text: '#e2e8f0',
  border: '1px solid #334155',
  shadow: '0 8px 28px rgba(15, 23, 42, 0.4)',
  focusRing: '0 0 0 2px #334155',
};

/**
 * `?` help — custom floating tooltip. Native `title` on <button> is often suppressed in Chrome/WebKit; portal avoids clipping.
 */
function BrandingHelpHint({ text }) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxW: 300 });
  const [focused, setFocused] = useState(false);
  const closeTimer = useRef(null);
  const hintUid = useId().replace(/:/g, '');

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxW = Math.min(300, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - maxW - 8));
    setPos({ top: r.bottom + 6, left, maxW });
  }, []);

  const openNow = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    place();
    setOpen(true);
  }, [place]);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  }, []);

  const keepOpen = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const onReposition = () => place();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, place]);

  const tooltip = open && (
    <div
      id={`cc-brand-hint-${hintUid}`}
      role="tooltip"
      onMouseEnter={keepOpen}
      onMouseLeave={() => setOpen(false)}
      style={{
        position: 'fixed',
        zIndex: 10050,
        top: pos.top,
        left: pos.left,
        maxWidth: pos.maxW,
        boxSizing: 'border-box',
        padding: '10px 12px',
        background: HINT_CHROME.panelBg,
        color: HINT_CHROME.text,
        border: HINT_CHROME.border,
        boxShadow: HINT_CHROME.shadow,
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.45,
        fontWeight: 500,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        pointerEvents: 'auto',
        maxHeight: 'min(42vh, 220px)',
        overflowY: 'auto',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {text}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={text}
        aria-describedby={open ? `cc-brand-hint-${hintUid}` : undefined}
        onClick={(e) => e.preventDefault()}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          setFocused(true);
          openNow();
        }}
        onBlur={() => {
          setFocused(false);
          scheduleClose();
        }}
        style={{
          width: 18,
          height: 18,
          minWidth: 18,
          borderRadius: 999,
          border: '1px solid var(--branding-app-surface-panel-border)',
          background: 'var(--branding-app-surface-panel)',
          color: 'var(--cc-text-muted)',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: 'help',
          fontFamily: 'system-ui, sans-serif',
          verticalAlign: 'middle',
          boxShadow: focused ? HINT_CHROME.focusRing : 'none',
          outline: 'none',
        }}
      >
        ?
      </button>
      {typeof document !== 'undefined' && tooltip ? createPortal(tooltip, document.body) : null}
    </>
  );
}

function FieldLabelRow({ label, hint }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 6,
        minHeight: 20,
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--cc-text-muted)', fontWeight: 500 }}>{label}</span>
      {hint ? <BrandingHelpHint text={hint} /> : null}
    </div>
  );
}

const HINT_LOGO_UPLOAD =
  'Use PNG, JPEG, WebP, or GIF — max 2 MB. Transparent PNGs usually work best. If your file is a solid rectangle on white, we recommend a white or black tile below (not None), and you can add corner rounding if the edges look harsh.';

const HINT_PRESETS =
  'Quick starting points. You can still set exact values with the sliders.';

const HINT_MAX_H =
  'Upper limit for how tall the logo can appear (px). Higher values suit tall or portrait marks.';

const HINT_MAX_W =
  'Upper limit for how wide the logo can appear (px). Higher values suit wordmarks. The logo will not overflow the nav area.';

const HINT_ALIGN =
  'When the image is narrower than the nav row, choose whether it lines up to the left, centre, or right.';

const HINT_CORNERS =
  'Rounds the corners of the image (and, when a tile is on, works together with the tile’s own rounding). Use for sharp square marks; leave at 0 if a white or black tile is enough. Most useful with “None” or boxy art.';

const HINT_PLATE =
  'Match the tile to your file: white background in the artwork → White tile; black or very dark background → Black tile. Tiles keep the nav consistent, especially on mobile. None: only when a plate would look wrong (e.g. a full-bleed mark that must sit directly on the bar). If unsure, try White or Black tile first.';

/** Default colour picker seed when none saved — matches DEFAULT_APP_PRIMARY (Crewchain chrome navy). */
export const BRANDING_EDITOR_DEFAULT_HEX = '#2b3540';

const EDITOR_DEFAULT_PARSED = parseHexColor(BRANDING_EDITOR_DEFAULT_HEX);

/**
 * Company logo + accent colour (Pro / Pro+). Sidebar logo, primary buttons, links, and Home plan strip.
 *
 * @param {{ compact?: boolean; onClose?: () => void; onSaved?: () => void; showPageChrome?: boolean; onBack?: () => void }} props
 */
export default function CompanyBrandingEditor({
  compact = false,
  onClose,
  onSaved,
  showPageChrome = false,
  onBack,
}) {
  const { user, userData } = useAuth();
  const { company, companyId, loading, refetchCompanyBranding } = useBranding();
  const [hexInput, setHexInput] = useState(BRANDING_EDITOR_DEFAULT_HEX);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [logoLocalPreviewUrl, setLogoLocalPreviewUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMaxH, setLogoMaxH] = useState(DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT);
  const [logoMaxW, setLogoMaxW] = useState(DEFAULT_SIDEBAR_LOGO_MAX_WIDTH);
  const [logoAlign, setLogoAlign] = useState(DEFAULT_SIDEBAR_LOGO_ALIGN);
  const [logoCornerR, setLogoCornerR] = useState(DEFAULT_LOGO_CORNER_RADIUS);
  const [logoBacking, setLogoBacking] = useState('none');
  const [teamSignOffStyleDraft, setTeamSignOffStyleDraft] = useState('blank');
  const teamSignOffRadioName = useId();
  const fileInputRef = useRef(null);

  const ownerIds = useMemo(() => {
    if (!company) return [];
    return Array.isArray(company.accountOwnerIds)
      ? company.accountOwnerIds
      : company.accountOwnerId
        ? [company.accountOwnerId]
        : [];
  }, [company]);

  const isAccountOwner = user?.uid && ownerIds.includes(user.uid);
  const planId = company?.planId || '';
  const isEligiblePlan = BRANDING_ELIGIBLE_PLAN_IDS.includes(planId);

  const savedParsed = parseHexColor(company?.branding?.primaryColor);
  const savedLogoUrl = typeof company?.branding?.logoUrl === 'string' ? company.branding.logoUrl : null;

  const savedLogoDims = useMemo(() => getSidebarLogoDimensions(company), [company]);
  const savedLogoAlign = useMemo(() => getSidebarLogoAlign(company), [company]);
  const savedLogoFrame = useMemo(() => getSidebarLogoFrame(company), [company]);

  useEffect(() => {
    if (!company) return;
    setHexInput(savedParsed || BRANDING_EDITOR_DEFAULT_HEX);
    const d = getSidebarLogoDimensions(company);
    setLogoMaxH(d.maxHeight);
    setLogoMaxW(d.maxWidth);
    setLogoAlign(getSidebarLogoAlign(company));
    const lf = getSidebarLogoFrame(company);
    setLogoCornerR(lf.cornerRadius);
    setLogoBacking(lf.backing);
    setTeamSignOffStyleDraft(
      getTeamSignOffSheetStyleFromCompany(company) ?? effectiveTeamSignOffSheetStyle(company),
    );
  }, [company, savedParsed]);

  useEffect(() => {
    return () => {
      if (logoLocalPreviewUrl) URL.revokeObjectURL(logoLocalPreviewUrl);
    };
  }, [logoLocalPreviewUrl]);

  const setLocalPreviewFile = (file) => {
    setLogoLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const previewTokens = useMemo(() => previewBrandingAccentOnly(hexInput), [hexInput]);

  const sandboxLogoUrl = logoLocalPreviewUrl || savedLogoUrl || null;

  const isDirtyLogoFit = useMemo(
    () =>
      logoMaxH !== savedLogoDims.maxHeight ||
      logoMaxW !== savedLogoDims.maxWidth ||
      logoAlign !== savedLogoAlign ||
      logoCornerR !== savedLogoFrame.cornerRadius ||
      logoBacking !== savedLogoFrame.backing,
    [
      logoMaxH,
      logoMaxW,
      logoAlign,
      logoCornerR,
      logoBacking,
      savedLogoDims,
      savedLogoAlign,
      savedLogoFrame,
    ],
  );

  const isDirtyAccent = useMemo(() => {
    const draft = parseHexColor(hexInput);
    const saved = savedParsed || '';
    const d = draft || '';
    if (d === saved) return false;
    if (!savedParsed && draft === EDITOR_DEFAULT_PARSED) return false;
    return true;
  }, [hexInput, savedParsed]);

  const isDirtyTeamSignOff = useMemo(() => {
    if (!company) return false;
    const compared =
      getTeamSignOffSheetStyleFromCompany(company) ?? effectiveTeamSignOffSheetStyle(company);
    return teamSignOffStyleDraft !== compared;
  }, [company, teamSignOffStyleDraft]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    if (text) setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleSaveAccent = async () => {
    const parsed = parseHexColor(hexInput);
    if (!parsed) {
      showMessage('Enter a valid hex colour (e.g. #2b3540) or use Reset to default.', 'error');
      return;
    }
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ primaryColor: parsed });
      await refetchCompanyBranding();
      showMessage('Accent colour saved.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not save branding', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAccent = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ primaryColor: null });
      await refetchCompanyBranding();
      setHexInput(BRANDING_EDITOR_DEFAULT_HEX);
      showMessage('Restored default accent colour.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not reset branding', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    const cid = normalizeCompanyIdForStorage(userData?.companyId ?? companyId);
    if (!cid) {
      showMessage('Company not loaded. Try again.', 'error');
      return;
    }
    if (!isAllowedBrandingLogoFile(file)) {
      showMessage('Use PNG, JPEG, WebP, or GIF.', 'error');
      return;
    }
    if (file.size > COMPANY_BRANDING_LOGO_MAX_BYTES) {
      showMessage('Logo must be 2MB or smaller.', 'error');
      return;
    }
    setLocalPreviewFile(file);
    setLogoBusy(true);
    setMessage({ text: '', type: '' });
    try {
      const appCheck = await ensureAppCheckToken(true);
      if (!appCheck.ok) {
        showMessage(appCheck.errorMessage || 'App Check failed. Try again after registering the debug token in Firebase Console.', 'error');
        return;
      }
      const path = companyBrandingLogoStoragePath(cid);
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file, { contentType: file.type || 'image/png' });
      const url = await getDownloadURL(sref);
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ logoUrl: url });
      await refetchCompanyBranding();
      setLocalPreviewFile(null);
      showMessage('Logo saved. It appears in the sidebar for your team.', 'success');
      onSaved?.();
    } catch (err) {
      setLocalPreviewFile(null);
      showMessage(err.message || 'Logo upload failed', 'error');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (logoLocalPreviewUrl) {
      setLocalPreviewFile(null);
      return;
    }
    if (!savedLogoUrl) return;
    setLogoBusy(true);
    setMessage({ text: '', type: '' });
    try {
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ clearLogo: true });
      await refetchCompanyBranding();
      showMessage('Logo removed.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not remove logo', 'error');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSaveLogoFit = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({
        logoMaxHeight: logoMaxH,
        logoMaxWidth: logoMaxW,
        logoAlign,
        logoCornerRadius: logoCornerR === 0 ? null : logoCornerR,
        logoBacking: logoBacking === 'none' ? null : logoBacking,
      });
      await refetchCompanyBranding();
      showMessage('Logo settings saved for the sidebar.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not save logo settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetLogoFit = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({
        logoMaxHeight: null,
        logoMaxWidth: null,
        logoAlign: null,
        logoCornerRadius: null,
        logoBacking: null,
      });
      await refetchCompanyBranding();
      setLogoMaxH(DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT);
      setLogoMaxW(DEFAULT_SIDEBAR_LOGO_MAX_WIDTH);
      setLogoAlign(DEFAULT_SIDEBAR_LOGO_ALIGN);
      setLogoCornerR(DEFAULT_LOGO_CORNER_RADIUS);
      setLogoBacking('none');
      showMessage('Restored default logo appearance.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not reset logo settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTeamSignOffStyle = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const appCheck = await ensureAppCheckToken(true);
      if (!appCheck.ok) {
        showMessage(appCheck.errorMessage || 'App Check failed.', 'error');
        return;
      }
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ teamSignOffSheetStyle: teamSignOffStyleDraft });
      await refetchCompanyBranding();
      showMessage('Team sign-off sheet preference saved.', 'success');
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not save sheet preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTeamSignOffStyle = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const appCheck = await ensureAppCheckToken(true);
      if (!appCheck.ok) {
        showMessage(appCheck.errorMessage || 'App Check failed.', 'error');
        return;
      }
      const updateCompanyBranding = httpsCallable(functions, 'updateCompanyBranding');
      await updateCompanyBranding({ teamSignOffSheetStyle: null });
      await refetchCompanyBranding();
      showMessage(
        'Sheet preference cleared. The app will use the default rule again (Crewchain sheet, or your logo on neutral when you have a logo).',
        'success',
      );
      onSaved?.();
    } catch (err) {
      showMessage(err.message || 'Could not reset sheet preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !company) {
    return <p style={{ color: 'var(--cc-text-muted)', margin: 0 }}>Loading…</p>;
  }

  if (!company || !isAccountOwner || !isEligiblePlan) {
    return null;
  }

  const gridStyle = compact
    ? { display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'stretch' }
    : {
        display: 'grid',
        gridTemplateColumns: 'minmax(340px, 1.7fr) minmax(260px, 1fr)',
        gap: 28,
        alignItems: 'start',
      };

  const previewBlock = (
    <div style={{ minWidth: 0, width: '100%' }}>
      <h2 style={{ fontSize: compact ? 15 : 17, fontWeight: 700, color: 'var(--cc-text-primary)', margin: '0 0 14px' }}>
        Live preview
      </h2>
      <BrandingSandbox
        tokens={previewTokens}
        prominent
        logoUrl={sandboxLogoUrl}
        logoMaxHeight={logoMaxH}
        logoMaxWidth={logoMaxW}
        logoAlign={logoAlign}
        logoCornerRadius={logoCornerR}
        logoBacking={logoBacking}
      />
    </div>
  );

  const hasLogoPreview = Boolean(logoLocalPreviewUrl || savedLogoUrl);

  const controlsBlock = (
    <div
      style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 16,
        padding: compact ? 18 : 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      {message.text && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 13,
            background: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
            color: message.type === 'error' ? '#b91c1c' : '#166534',
          }}
        >
          {message.text}
        </div>
      )}

      <div>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--cc-text-primary)', margin: '0 0 8px' }}>Company logo</h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            margin: '0 0 14px',
            lineHeight: 1.5,
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--cc-text-muted)', margin: 0, flex: 1, minWidth: 0 }}>
            Appears in the app navigation for your organisation, and for subcontractors when they work on your company’s
            projects (where your branding is shown).
          </p>
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <BrandingHelpHint text={HINT_LOGO_UPLOAD} />
          </div>
        </div>
        <div
          style={{
            border: '1px solid var(--branding-app-surface-panel-border)',
            borderRadius: 10,
            padding: 12,
            background: 'var(--branding-app-surface-panel)',
            color: 'var(--branding-app-surface-panel-fg)',
            marginBottom: 12,
            minHeight: 88,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarLogoAlignToFlexJustify(logoAlign),
          }}
        >
          {hasLogoPreview ? (
            <BrandedSidebarLogo
              src={logoLocalPreviewUrl || savedLogoUrl}
              alt="Logo preview"
              isCustomCompanyLogo
              maxWidth={Math.min(logoMaxW, 260)}
              maxHeight={logoMaxH}
              cornerRadius={logoCornerR}
              backing={logoBacking}
            />
          ) : (
            <span style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-muted)' }}>No logo uploaded</span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={COMPANY_BRANDING_LOGO_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleLogoFile}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button
            type="button"
            disabled={logoBusy}
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: logoBusy ? '#94a3b8' : 'var(--branding-app-primary)',
              color: logoBusy ? '#fff' : 'var(--branding-app-primary-fg)',
              fontWeight: 600,
              fontSize: 14,
              cursor: logoBusy ? 'wait' : 'pointer',
            }}
          >
            {logoBusy ? 'Uploading…' : 'Upload logo'}
          </button>
          {hasLogoPreview && (
            <button
              type="button"
              disabled={logoBusy}
              onClick={handleRemoveLogo}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                background: 'var(--cc-card-bg)',
                color: 'var(--cc-text-secondary)',
                fontSize: 14,
                cursor: logoBusy ? 'wait' : 'pointer',
              }}
            >
              Remove logo
            </button>
          )}
        </div>

        {hasLogoPreview && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 20,
              borderTop: '1px solid var(--branding-app-surface-panel-border)',
            }}
          >
            <div
              style={{
                background: 'var(--branding-app-surface-panel)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 10,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--cc-text-muted)', fontWeight: 600 }}>Quick size</span>
                  <BrandingHelpHint text={HINT_PRESETS} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { label: 'Compact', h: 44, w: 180 },
                    { label: 'Default', h: DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT, w: DEFAULT_SIDEBAR_LOGO_MAX_WIDTH },
                    { label: 'Large', h: 72, w: 232 },
                    { label: 'Extra', h: 88, w: 260 },
                  ].map(({ label, h, w }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        setLogoMaxH(h);
                        setLogoMaxW(w);
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: logoMaxH === h && logoMaxW === w ? '2px solid var(--cc-text-primary)' : '1px solid var(--branding-app-surface-panel-border)',
                        background: 'var(--cc-card-bg)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--cc-text-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 12,
                }}
              >
                <div>
                  <FieldLabelRow label="Max height (px)" hint={HINT_MAX_H} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="range"
                      min={SIDEBAR_LOGO_MAX_HEIGHT_MIN}
                      max={SIDEBAR_LOGO_MAX_HEIGHT_MAX}
                      value={logoMaxH}
                      onChange={(e) => setLogoMaxH(Number(e.target.value))}
                      style={{ flex: 1, minWidth: 160 }}
                    />
                    <input
                      type="number"
                      min={SIDEBAR_LOGO_MAX_HEIGHT_MIN}
                      max={SIDEBAR_LOGO_MAX_HEIGHT_MAX}
                      value={logoMaxH}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isNaN(n)) return;
                        setLogoMaxH(
                          Math.min(SIDEBAR_LOGO_MAX_HEIGHT_MAX, Math.max(SIDEBAR_LOGO_MAX_HEIGHT_MIN, Math.round(n))),
                        );
                      }}
                      style={{ width: 72, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)', fontSize: 13, background: 'var(--cc-card-bg)' }}
                    />
                  </div>
                </div>
                <div>
                  <FieldLabelRow label="Max width (px)" hint={HINT_MAX_W} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="range"
                      min={SIDEBAR_LOGO_MAX_WIDTH_MIN}
                      max={SIDEBAR_LOGO_MAX_WIDTH_MAX}
                      value={logoMaxW}
                      onChange={(e) => setLogoMaxW(Number(e.target.value))}
                      style={{ flex: 1, minWidth: 160 }}
                    />
                    <input
                      type="number"
                      min={SIDEBAR_LOGO_MAX_WIDTH_MIN}
                      max={SIDEBAR_LOGO_MAX_WIDTH_MAX}
                      value={logoMaxW}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isNaN(n)) return;
                        setLogoMaxW(
                          Math.min(SIDEBAR_LOGO_MAX_WIDTH_MAX, Math.max(SIDEBAR_LOGO_MAX_WIDTH_MIN, Math.round(n))),
                        );
                      }}
                      style={{ width: 72, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)', fontSize: 13, background: 'var(--cc-card-bg)' }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <FieldLabelRow label="Alignment" hint={HINT_ALIGN} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { key: 'start', label: 'Left' },
                    { key: 'center', label: 'Center' },
                    { key: 'end', label: 'Right' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setLogoAlign(key)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: logoAlign === key ? '2px solid var(--cc-text-primary)' : '1px solid var(--branding-app-surface-panel-border)',
                        background: 'var(--cc-card-bg)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--cc-text-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div
                style={{
                  borderTop: '1px solid var(--branding-app-surface-panel-border)',
                  paddingTop: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <p style={{ fontSize: 11, color: 'var(--branding-app-surface-panel-hint)', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Framing
                </p>
                <div>
                  <FieldLabelRow label="Background behind logo" hint={HINT_PLATE} />
                  <p style={{ fontSize: 12, color: 'var(--cc-text-muted)', margin: '0 0 10px', lineHeight: 1.45 }}>
                    <strong style={{ color: 'var(--cc-text-secondary)' }}>Recommendation:</strong> if your logo file already sits on a{' '}
                    <strong>white</strong> background, choose <strong>White tile</strong>. If it already sits on a{' '}
                    <strong>black</strong> or very dark background, choose <strong>Black tile</strong>. For a consistent look
                    in the nav, we usually suggest a tile rather than None.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                    {[
                      { key: 'none', label: 'None' },
                      { key: 'white', label: 'White tile' },
                      { key: 'black', label: 'Black tile' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setLogoBacking(key)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 8,
                          border: logoBacking === key ? '2px solid var(--cc-text-primary)' : '1px solid var(--branding-app-surface-panel-border)',
                          background: 'var(--cc-card-bg)',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--cc-text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabelRow label="Corner radius (px)" hint={HINT_CORNERS} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="range"
                      min={0}
                      max={LOGO_CORNER_RADIUS_MAX}
                      value={logoCornerR}
                      onChange={(e) => setLogoCornerR(Number(e.target.value))}
                      style={{ flex: 1, minWidth: 160 }}
                    />
                    <input
                      type="number"
                      min={0}
                      max={LOGO_CORNER_RADIUS_MAX}
                      value={logoCornerR}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isNaN(n)) return;
                        setLogoCornerR(Math.min(LOGO_CORNER_RADIUS_MAX, Math.max(0, Math.round(n))));
                      }}
                      style={{ width: 56, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)', fontSize: 13, background: 'var(--cc-card-bg)' }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  paddingTop: 4,
                }}
              >
                <button
                  type="button"
                  disabled={saving || logoBusy || !isDirtyLogoFit}
                  onClick={handleSaveLogoFit}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: saving || !isDirtyLogoFit ? '#94a3b8' : 'var(--branding-app-primary)',
                    color: saving || !isDirtyLogoFit ? '#fff' : 'var(--branding-app-primary-fg)',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: saving || !isDirtyLogoFit ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Saving…' : 'Save logo settings'}
                </button>
                <button
                  type="button"
                  disabled={saving || logoBusy}
                  onClick={handleResetLogoFit}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    background: 'var(--cc-card-bg)',
                    color: 'var(--cc-text-secondary)',
                    fontSize: 14,
                    cursor: saving ? 'wait' : 'pointer',
                  }}
                >
                  Reset to defaults
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--branding-app-surface-panel-border)', paddingTop: 24, marginTop: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--cc-text-primary)', margin: '0 0 8px' }}>
          Team document sign-off sheet
        </h2>
        <p style={{ fontSize: 13, color: 'var(--cc-text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
          Extra pages appended when workers sign your team sign-off PDFs.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {[
            {
              value: 'blank',
              label: 'Neutral (no logo)',
              desc: 'Clean layout without any branding on the sheet.',
            },
            {
              value: 'branded',
              label: 'Your logo on neutral sheet',
              desc: 'Neutral layout with your company logo automatically placed in the bottom-right margin.',
            },
          ].map((opt) => (
            <label
              key={opt.value}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}
            >
              <input
                type="radio"
                name={teamSignOffRadioName}
                checked={teamSignOffStyleDraft === opt.value}
                onChange={() => setTeamSignOffStyleDraft(opt.value)}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontWeight: 600, color: 'var(--cc-text-secondary)' }}>{opt.label}</span>
                <span style={{ display: 'block', color: 'var(--cc-text-muted)', marginTop: 2 }}>{opt.desc}</span>
              </span>
            </label>
          ))}
        </div>
        {teamSignOffStyleDraft === 'branded' && !hasLogoPreview && (
          <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 12px', lineHeight: 1.45 }}>
            Upload a company logo above to use this option fully. Until then, sign-off PDFs will use the neutral sheet
            without a logo.
          </p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <button
            type="button"
            disabled={saving || logoBusy || !isDirtyTeamSignOff}
            onClick={handleSaveTeamSignOffStyle}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              background: saving || !isDirtyTeamSignOff ? '#94a3b8' : 'var(--branding-app-primary)',
              color: saving || !isDirtyTeamSignOff ? '#fff' : 'var(--branding-app-primary-fg)',
              fontWeight: 600,
              fontSize: 14,
              cursor: saving || !isDirtyTeamSignOff ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save sheet preference'}
          </button>
          <button
            type="button"
            disabled={saving || logoBusy || getTeamSignOffSheetStyleFromCompany(company) == null}
            onClick={handleResetTeamSignOffStyle}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
              background: 'var(--cc-card-bg)',
              color: 'var(--cc-text-secondary)',
              fontSize: 14,
              cursor: saving || getTeamSignOffSheetStyleFromCompany(company) == null ? 'not-allowed' : 'pointer',
            }}
          >
            Use automatic sheet rule
          </button>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--branding-app-surface-panel-border)', paddingTop: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--cc-text-primary)', margin: '0 0 8px' }}>Accent colour</h2>
        <p style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)', margin: '0 0 16px', lineHeight: 1.45 }}>
          Used for primary buttons, links, and key actions app-wide, plus the Home plan strip. The same accent is shown to
          subcontractors when they work on your company’s projects, wherever your branding appears.
        </p>

        <BrandingColorField
          label="Accent"
          value={hexInput}
          onChange={setHexInput}
          fallbackHex={BRANDING_EDITOR_DEFAULT_HEX}
          placeholder="#2b3540"
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
          <button
            type="button"
            disabled={saving || logoBusy || !isDirtyAccent}
            onClick={handleSaveAccent}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              background: saving || !isDirtyAccent ? '#94a3b8' : 'var(--branding-app-primary)',
              color: saving || !isDirtyAccent ? '#fff' : 'var(--branding-app-primary-fg)',
              fontWeight: 600,
              fontSize: 14,
              cursor: saving || !isDirtyAccent ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save accent'}
          </button>
          <button
            type="button"
            disabled={saving || logoBusy}
            onClick={handleResetAccent}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
              background: 'var(--cc-card-bg)',
              color: 'var(--cc-text-secondary)',
              fontSize: 14,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            Reset accent to default
          </button>
        </div>
        <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--branding-app-surface-panel-hint)', lineHeight: 1.5 }}>
          Reset restores Crewchain&apos;s default accent for your organisation and for subs on your projects (logo unchanged).
        </p>
      </div>
    </div>
  );

  return (
    <div>
      {(showPageChrome || onClose) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: compact ? 16 : 24,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {onBack && showPageChrome && (
              <button
                type="button"
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--cc-text-muted)',
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: 0,
                  marginBottom: 12,
                  display: 'block',
                }}
              >
                ← Back to settings
              </button>
            )}
            {showPageChrome && (
              <>
                <h1 style={{ fontSize: compact ? 20 : 26, fontWeight: 700, color: 'var(--cc-text-primary)', margin: '0 0 8px' }}>
                  Company branding
                </h1>
                <p style={{ margin: 0, color: 'var(--cc-text-muted)', fontSize: compact ? 13 : 15, lineHeight: 1.5 }}>
                  Upload a company logo, fine-tune its layout, and set an accent colour for your team and for subcontractors
                  on your projects. The live preview below uses your accent draft and the default page background.
                </p>
              </>
            )}
            {onClose && !showPageChrome && (
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--cc-text-primary)', margin: 0 }}>Company branding</h2>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                background: 'var(--cc-card-bg)',
                cursor: 'pointer',
                fontSize: 20,
                lineHeight: 1,
                color: 'var(--cc-text-muted)',
              }}
            >
              ×
            </button>
          )}
        </div>
      )}

      <div style={gridStyle}>
        {previewBlock}
        {controlsBlock}
      </div>
    </div>
  );
}
